/**
 * CandidateEditDialog (Slice 3C of #860).
 *
 * Modal form for creating a new user-authored candidate ("+ Add
 * manually" in the Candidates UI) OR editing an existing one
 * (click a row in the list). Single component for both flows;
 * `mode` discriminates.
 *
 * Edits content only — name, description, preconditions, steps, tags.
 * Priority and test type are edited inline on the candidate card
 * (#2126), not here, so each field has a single editor. A newly
 * created candidate starts with no test type; the operator sets it
 * inline on the card afterward.
 *
 * Layout follows the same shape as TestAuthoringTemplateDialog:
 * inline form inside `<DialogBody>`, save/cancel footer. The
 * steps editor is the only structural piece worth its own
 * sub-component — each step has an optional `perform` + an
 * ordered array of freeform `expect` assertions, and the user
 * needs to add/remove both axes.
 *
 * Save sends the full form payload as a PATCH (existing) or
 * POST (new) via `applicationsAPI.{update,create}Candidate`. The
 * parent re-fetches on success.
 *
 * Validation is a defense-in-depth mirror of the route validator:
 * required `name` + `description`, length caps, freeform expect
 * shape on steps. The Save button is disabled until name +
 * description are non-empty.
 */
import { useState, type FormEvent } from 'react'
import { X, Plus } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { applicationsAPI } from '@/lib/api'
import {
  CANDIDATE_TEST_TAGS,
  type CandidateTest,
  type CandidateTestStep,
  type CandidateTestTag,
} from '@/types'

interface Props {
  appId: string
  /** When set, the dialog opens in edit mode with the candidate's
   * current values pre-filled. When undefined, opens in create
   * mode (POST). */
  candidate?: CandidateTest
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fired after a successful save so the parent can re-fetch. */
  onSaved: () => void | Promise<void>
}

export function CandidateEditDialog({ appId, candidate, open, onOpenChange, onSaved }: Props) {
  const isEdit = candidate !== undefined

  // Form state initialized from the candidate (edit) or to empty
  // (create). `useState` initializers run once per mount; when the
  // parent re-opens with a different `candidate`, the form should
  // re-init — so we key the dialog on the candidate id at the
  // parent (see CandidatesSection).
  const [name, setName] = useState(candidate?.name ?? '')
  const [description, setDescription] = useState(candidate?.description ?? '')
  const [preconditions, setPreconditions] = useState<string[]>(candidate?.preconditions ?? [])
  const [steps, setSteps] = useState<CandidateTestStep[]>(candidate?.steps ?? [])
  const [tags, setTags] = useState<CandidateTestTag[]>(candidate?.tags ?? [])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggleTag(t: CandidateTestTag) {
    setTags(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]))
  }

  function addPrecondition() {
    setPreconditions(prev => [...prev, ''])
  }
  function setPreconditionAt(i: number, v: string) {
    setPreconditions(prev => prev.map((p, idx) => (idx === i ? v : p)))
  }
  function removePreconditionAt(i: number) {
    setPreconditions(prev => prev.filter((_, idx) => idx !== i))
  }

  function addStep() {
    setSteps(prev => [...prev, { expect: [{ kind: 'freeform', description: '' }] }])
  }
  function removeStepAt(i: number) {
    setSteps(prev => prev.filter((_, idx) => idx !== i))
  }
  function setStepPerformAt(i: number, v: string) {
    setSteps(prev => prev.map((s, idx) => (idx === i ? { ...s, perform: v } : s)))
  }
  function addExpectAt(i: number) {
    setSteps(prev =>
      prev.map((s, idx) =>
        idx === i ? { ...s, expect: [...s.expect, { kind: 'freeform', description: '' }] } : s,
      ),
    )
  }
  function setExpectAt(stepIdx: number, expectIdx: number, v: string) {
    setSteps(prev =>
      prev.map((s, idx) =>
        idx === stepIdx
          ? {
              ...s,
              expect: s.expect.map((e, j) =>
                j === expectIdx ? { ...e, description: v } : e,
              ),
            }
          : s,
      ),
    )
  }
  function removeExpectAt(stepIdx: number, expectIdx: number) {
    setSteps(prev =>
      prev.map((s, idx) =>
        idx === stepIdx ? { ...s, expect: s.expect.filter((_, j) => j !== expectIdx) } : s,
      ),
    )
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // Sanitize the payload before sending: trim strings, drop
      // empty preconditions, drop empty expects, drop empty
      // steps. Mirrors the route validator's permissive shape
      // (it accepts and trims; the client trims first for
      // immediate feedback).
      const cleanPreconditions = preconditions.map(p => p.trim()).filter(Boolean)
      const cleanSteps: CandidateTestStep[] = steps
        .map(s => ({
          perform: s.perform?.trim() || undefined,
          expect: s.expect
            .map(e => ({ kind: 'freeform' as const, description: e.description.trim() }))
            .filter(e => e.description.length > 0),
        }))
        .filter(s => s.expect.length > 0)

      const cleanName = name.trim()
      const cleanDescription = description.trim()

      if (isEdit && candidate) {
        // Build a MINIMAL patch — only include fields whose values
        // actually changed vs the original candidate. The server's
        // updateCandidate flips `userEdited: true` + invalidates the
        // embedding on any content-field change in the patch, so a
        // tag-only edit that ALSO sent `preconditions: null` /
        // `steps: null` (the defaults from this form's state when
        // those arrays are empty) would incorrectly mark a vanilla
        // proposer candidate as user-edited. Diffing here keeps the
        // re-propose semantics honest. Per Copilot review on #877.
        const patch: Parameters<typeof applicationsAPI.updateCandidate>[2] = {}
        if (cleanName !== candidate.name) patch.name = cleanName
        if (cleanDescription !== candidate.description) patch.description = cleanDescription
        if (!sameStringArrays(cleanPreconditions, candidate.preconditions ?? [])) {
          patch.preconditions = cleanPreconditions.length > 0 ? cleanPreconditions : null
        }
        if (!sameSteps(cleanSteps, candidate.steps ?? [])) {
          patch.steps = cleanSteps.length > 0 ? cleanSteps : null
        }
        if (!sameStringArrays(tags, candidate.tags)) patch.tags = tags
        // Avoid an empty PATCH (no fields changed). The server
        // would no-op, but the round-trip is wasted and the parent's
        // re-fetch on success would still fire — confusing UX.
        if (Object.keys(patch).length === 0) {
          onOpenChange(false)
          return
        }
        await applicationsAPI.updateCandidate(appId, candidate._id, patch)
      } else {
        await applicationsAPI.createCandidate(appId, {
          name: cleanName,
          description: cleanDescription,
          preconditions: cleanPreconditions.length > 0 ? cleanPreconditions : null,
          steps: cleanSteps.length > 0 ? cleanSteps : null,
          tags,
        })
      }
      await onSaved()
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const canSave = name.trim().length > 0 && description.trim().length > 0 && !submitting

/** Compare two string arrays for deep equality (order-sensitive,
 * post-trim). Used to skip unchanged fields from the PATCH payload
 * in edit mode. */
function sameStringArrays(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/** Compare two `steps` arrays for structural equality. Both `perform`
 * and the ordered `expect[]` descriptions have to match. Steps are
 * the only nested field on the candidate; an array-of-strings
 * equality check is sufficient for everything else. */
function sameSteps(a: readonly CandidateTestStep[], b: readonly CandidateTestStep[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const sa = a[i]!
    const sb = b[i]!
    if ((sa.perform ?? '') !== (sb.perform ?? '')) return false
    if (sa.expect.length !== sb.expect.length) return false
    for (let j = 0; j < sa.expect.length; j++) {
      if (sa.expect[j]!.description !== sb.expect[j]!.description) return false
    }
  }
  return true
}

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit this test' : 'Add a test'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="candidate-name">Name</Label>
              <Input
                id="candidate-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='e.g. "User logs in with MFA enabled"'
                maxLength={200}
                disabled={submitting}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="candidate-description">Description</Label>
              <Textarea
                id="candidate-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="1–2 sentences: what defect would this test catch?"
                rows={3}
                maxLength={1000}
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags (multi-select)</Label>
              <div className="flex flex-wrap gap-2">
                {/* Chip-styled toggle buttons — NOT wrapped in
                    <label> (the shared <Checkbox /> internally
                    renders its own <label>; nesting them produces
                    invalid HTML + confuses assistive tech, per
                    Copilot review on #877). The selected state is
                    surfaced by border + bg color rather than a
                    nested checkbox affordance. */}
                {CANDIDATE_TEST_TAGS.map(t => {
                  const selected = tags.includes(t)
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      disabled={submitting}
                      aria-pressed={selected}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        selected
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-card hover:bg-muted/40'
                      }`}
                    >
                      {t}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Preconditions (optional)</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addPrecondition} disabled={submitting}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add precondition
                </Button>
              </div>
              {preconditions.length === 0 && (
                <p className="text-xs text-muted-foreground italic">None — this test doesn&apos;t need anything set up first.</p>
              )}
              {preconditions.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={p}
                    onChange={e => setPreconditionAt(i, e.target.value)}
                    placeholder='e.g. "User has MFA configured"'
                    disabled={submitting}
                  />
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => removePreconditionAt(i)} disabled={submitting}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Steps (optional)</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addStep} disabled={submitting}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add step
                </Button>
              </div>
              {steps.length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  None — the planner will fill steps in from name + description alone.
                </p>
              )}
              {steps.map((step, i) => (
                <div key={i} className="space-y-2 p-3 border border-border rounded-md bg-muted/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Step {i + 1}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeStepAt(i)} disabled={submitting}>
                      <X className="h-3 w-3" />
                      Remove step
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`step-${i}-perform`}>Perform (optional)</Label>
                    <Input
                      id={`step-${i}-perform`}
                      value={step.perform ?? ''}
                      onChange={e => setStepPerformAt(i, e.target.value)}
                      placeholder='e.g. "Submit the login form"'
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Expect (at least one)</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => addExpectAt(i)} disabled={submitting}>
                        <Plus className="h-3 w-3 mr-1" />
                        Add expect
                      </Button>
                    </div>
                    {step.expect.map((e, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <Input
                          value={e.description}
                          onChange={ev => setExpectAt(i, j, ev.target.value)}
                          placeholder='e.g. "MFA prompt appears"'
                          disabled={submitting}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => removeExpectAt(i, j)}
                          disabled={submitting || step.expect.length <= 1}
                          title={step.expect.length <= 1 ? 'A step needs at least one expect' : 'Remove expect'}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add test'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
